class Task {
  constructor(data = {}) {
    this.id = data.id || data._id || null;
    this._id = data._id || data.id || null;
    this.taskId = data.taskId || `TSK${Date.now()}`;
    this.projectId = data.projectId || null;
    
    // Backend expects 'title' not 'taskName'
    this.title = data.title || data.taskName || '';
    this.taskName = data.taskName || data.title || '';
    
    this.description = data.description || '';
    
    // assignedTo should be ObjectId (or null), not a string name
    this.assignedTo = data.assignedTo || null;
    
    this.status = data.status || 'To Do';
    this.priority = data.priority || 'Medium';
    this.startDate = data.startDate || '';
    this.dueDate = data.dueDate || '';
    this.completedDate = data.completedDate || null;
    this.estimatedHours = data.estimatedHours || 0;
    this.actualHours = data.actualHours || 0;
    
    // Backend uses 'progress' field
    this.progress = data.progress || 0;
    
    this.tags = data.tags || [];
    this.dependencies = data.dependencies || [];
    this.comments = data.comments || [];
    this.createdAt = data.createdAt || new Date().toISOString();
  }

  getStatusColor() {
    const colors = {
      'To Do': '#6b7280',
      'In Progress': '#3b82f6',
      'Review': '#f59e0b',
      'Completed': '#10b981',
      'Blocked': '#ef4444'
    };
    return colors[this.status] || '#6b7280';
  }

  getPriorityColor() {
    const colors = { 
      'High': '#ef4444',
      'Critical': '#dc2626',
      'Medium': '#f59e0b', 
      'Low': '#3b82f6' 
    };
    return colors[this.priority] || '#6b7280';
  }

  isOverdue() {
    if (!this.dueDate || this.status === 'Completed') return false;
    return new Date(this.dueDate) < new Date();
  }

  getDaysUntilDue() {
    if (!this.dueDate) return 0;
    const due = new Date(this.dueDate);
    const now = new Date();
    return Math.ceil((due - now) / (1000 * 60 * 60 * 24));
  }

  // Get assignee name for display
  getAssigneeName() {
    if (!this.assignedTo) return 'Unassigned';
    if (typeof this.assignedTo === 'string') return this.assignedTo;
    if (this.assignedTo.name) return this.assignedTo.name;
    return 'Unassigned';
  }

  validate() {
    const errors = [];
    
    // Backend requires 'title'
    if (!this.title?.trim() && !this.taskName?.trim()) {
      errors.push('Task title is required');
    }
    
    if (!this.projectId) {
      errors.push('Project is required');
    }
    
    if (!this.dueDate) {
      errors.push('Due date is required');
    }
    
    // Validate status enum
    const validStatuses = ['To Do', 'In Progress', 'Review', 'Completed', 'Blocked'];
    if (!validStatuses.includes(this.status)) {
      errors.push(`Status must be one of: ${validStatuses.join(', ')}`);
    }
    
    // Validate priority enum
    const validPriorities = ['Low', 'Medium', 'High', 'Critical'];
    if (!validPriorities.includes(this.priority)) {
      errors.push(`Priority must be one of: ${validPriorities.join(', ')}`);
    }
    
    return { isValid: errors.length === 0, errors };
  }

  // Convert to backend API format
  toJSON() {
    const json = {
      title: this.title || this.taskName,
      description: this.description,
      projectId: this.projectId,
      status: this.status,
      priority: this.priority,
      dueDate: this.dueDate,
      estimatedHours: this.estimatedHours,
      actualHours: this.actualHours,
      progress: this.progress
    };

    // Only include assignedTo if it's a valid ObjectId (not empty)
    if (this.assignedTo && this.assignedTo !== '') {
      json.assignedTo = this.assignedTo;
    }

    // Only include startDate if provided
    if (this.startDate) {
      json.startDate = this.startDate;
    }

    // Only include completedDate if provided
    if (this.completedDate) {
      json.completedDate = this.completedDate;
    }

    if (this.tags && this.tags.length > 0) {
      json.tags = this.tags;
    }

    if (this.dependencies && this.dependencies.length > 0) {
      json.dependencies = this.dependencies;
    }

    return json;
  }

  static fromJSON(json) {
    return new Task(json);
  }
}

export default Task;